import type { TenantPublicEInvoicePolicy } from '@/services/api/tenant-public-settings-api'

const E_INVOICE_ADMIN_BUYER_TYPES = ['business', 'public_authority'] as const
const MAX_E_INVOICE_POLICY_CLOCK_SKEW_MS = 5 * 60 * 1000
const MAX_E_INVOICE_POLICY_LEASE_MS = 24 * 60 * 60 * 1000
const SHA_256_PATTERN = /^[a-f0-9]{64}$/

type EInvoiceAdminBuyerType = (typeof E_INVOICE_ADMIN_BUYER_TYPES)[number]

function isAdminBuyerType(value: string): value is EInvoiceAdminBuyerType {
  return E_INVOICE_ADMIN_BUYER_TYPES.includes(value as EInvoiceAdminBuyerType)
}

function hasApprovedProviderRoute(policy: TenantPublicEInvoicePolicy): boolean {
  if (policy.delivery?.enabled !== true || !Array.isArray(policy.delivery.routes)) {
    return false
  }
  return policy.delivery.routes.some((route) => {
    const buyerType = String(route.buyerType || '')
    const providerId = String(route.providerId || '').trim().toLowerCase()
    const format = String(route.format || '').trim()
    return (
      isAdminBuyerType(buyerType) &&
      providerId !== '' &&
      !['manual', 'manual_export', 'manual-export'].includes(providerId) &&
      format !== ''
    )
  })
}

function hasEnabledAdminBuyerType(policy: TenantPublicEInvoicePolicy): boolean {
  return E_INVOICE_ADMIN_BUYER_TYPES.some((buyerType) => policy.buyerTypes?.[buyerType]?.enabled === true)
}

function hasActiveControlPlaneEvidence(policy: TenantPublicEInvoicePolicy): boolean {
  if (!Number.isSafeInteger(policy.policyVersion) || Number(policy.policyVersion) < 1) {
    return false
  }
  if (!SHA_256_PATTERN.test(String(policy.revision || ''))
    || !SHA_256_PATTERN.test(String(policy.projectionHash || ''))) {
    return false
  }
  const issuedAtMs = Date.parse(String(policy.issuedAt || ''))
  const leaseExpiresAtMs = Date.parse(String(policy.leaseExpiresAt || ''))
  const nowMs = Date.now()
  return (
    Number.isFinite(issuedAtMs) &&
    Number.isFinite(leaseExpiresAtMs) &&
    leaseExpiresAtMs > issuedAtMs &&
    leaseExpiresAtMs - issuedAtMs <= MAX_E_INVOICE_POLICY_LEASE_MS &&
    issuedAtMs <= nowMs + MAX_E_INVOICE_POLICY_CLOCK_SKEW_MS &&
    nowMs < leaseExpiresAtMs
  )
}

export function isTenantEInvoiceSetupComplete(policy: TenantPublicEInvoicePolicy | null | undefined): boolean {
  if (!policy) {
    return false
  }
  return (
    String(policy.mode || '').toLowerCase() === 'b2b_b2g' &&
    String(policy.approvalStatus || '').toLowerCase() === 'approved' &&
    hasActiveControlPlaneEvidence(policy) &&
    hasEnabledAdminBuyerType(policy) &&
    hasApprovedProviderRoute(policy)
  )
}
